import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Truck, Globe, MapPin, Phone, Mail, Shield, Clock, CheckCircle2, Box } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const ShippingCanadaToAfricaPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', weight: '' });
  const { toast } = useToast();
  
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  
  const handleSubmit = (e) => {
    e.preventDefault();
    toast({ 
      title: "Quote Request Received!", 
      description: "We'll contact you within 24 hours with shipping rates to Africa." 
    });
    setFormData({ name: '', email: '', phone: '', weight: '' });
  };

  const services = [
    'Parcels and packages to Africa',
    'Document courier services',
    'Personal belongings',
    'Household goods',
    'Business cargo',
    'Barrel shipping',
    'Gift parcels',
    'Essential goods'
  ];

  const africanRegions = [
    'West Africa', 'East Africa', 'Southern Africa', 'North Africa', 'Central Africa'
  ];

  return (
    <>
      <Helmet>
        <title>Shipping from Canada to Africa | International Courier | Shippzy</title>
        <meta name="description" content="Reliable shipping from Canada to African countries. Parcels, documents, barrels, cargo. Professional international courier service." />
        <link rel="canonical" href="https://www.shippzy.ca/shipping-canada-to-africa" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-orange-900 via-red-900 to-yellow-800 text-white py-24 overflow-hidden">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <div className="inline-block mb-4 px-4 py-2 bg-orange-600/30 border border-orange-400/30 rounded-full">
                  <span className="text-orange-200 text-sm font-semibold">🌍 Canada to Africa Shipping</span>
                </div>
                <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                  Shipping from Canada
                  <span className="block bg-gradient-to-r from-orange-300 to-yellow-300 bg-clip-text text-transparent">to Africa</span>
                </h1>
                <p className="text-xl text-gray-200 mb-8">
                  Professional international courier service to African destinations. Parcels, cargo, barrels, and household goods shipping.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white px-8 py-6 text-lg shadow-xl"
                    onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                  >
                    Get Free Quote
                  </Button>
                  <Button 
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                    onClick={() => window.location.href = '/contact'}
                  >
                    Contact Us
                  </Button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="hidden md:block"
              >
                <motion.div
                  animate={{ y: [0, -20, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-3xl p-8 shadow-2xl"
                >
                  <Package className="w-32 h-32 text-white mx-auto mb-4" />
                  <div className="text-center">
                    <div className="text-4xl font-bold text-white mb-2">Reliable</div>
                    <div className="text-orange-200">Delivery to Africa</div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Barrel Shipping Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Barrel Shipping to Africa
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Ship household goods, personal belongings, and cargo in barrels to African destinations
              </p>
            </motion.div>

            <div className="bg-orange-50 border-l-4 border-orange-600 p-8 rounded-lg max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <Box className="w-12 h-12 text-orange-600 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-gray-900 text-xl mb-3">International Barrel Shipping</h3>
                  <p className="text-gray-700 mb-4">
                    Shippzy offers barrel shipping services to select African destinations. Perfect for sending household goods, clothing, food items, and personal belongings.
                  </p>
                  <Button
                    className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white"
                    onClick={() => window.location.href = '/international-barrel-shipping'}
                  >
                    Learn More About Barrel Shipping
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services & Regions */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-3">
                    <Package className="w-8 h-8 text-orange-600" />
                    What We Ship to Africa
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {services.map((service, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{service}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-l-4 border-orange-500">
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-3">
                    <MapPin className="w-8 h-8 text-orange-600" />
                    African Regions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {africanRegions.map((region, index) => (
                      <div key={index} className="flex items-center gap-2 text-gray-700">
                        <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                        <span>{region}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 mt-4">
                    Contact us for specific country availability and shipping options.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Quote Form */}
        <section id="quote-form" className="py-20 bg-gradient-to-br from-orange-600 via-orange-700 to-orange-800">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="shadow-2xl">
              <CardHeader>
                <CardTitle className="text-3xl text-center">Get Free Quote to Africa</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your Name"
                    className="h-12"
                  />
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email"
                    className="h-12"
                  />
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Phone"
                    className="h-12"
                  />
                  <Input
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    required
                    placeholder="Weight/Destination (e.g., 10 kg to Nigeria)"
                    className="h-12"
                  />
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-orange-600 to-orange-700 text-white h-14 text-lg font-semibold"
                  >
                    Get Quote Now
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </>
  );
};

export default ShippingCanadaToAfricaPage;